<template>
  <div class="ui-tree__search">
    <div class="ui-tree__search-input">
      <UIPanelSearch
        v-model="search"
        v-model:storage="modelValue"
        :placeholder
        :size
        :min-length="3"
        @keydown="handleKeyDown"
      />
    </div>
    <template v-if="search">
      <UIPreloader v-if="isLoading" />
      <template v-else>
        <span class="ui-tree__search-result">
          {{ displayNumberFound }}
        </span>
        <template v-if="numberFound > 1 && !hideArrows">
          <UIButtonIcon
            :leading="ArrowUpIcon"
            :size
            variant="gray"
            fill="outlined"
            class="ui-tree__search-arrow"
            @click="handleClickUp"
          />
          <UIButtonIcon
            :leading="ArrowDownIcon"
            :size
            variant="gray"
            fill="outlined"
            class="ui-tree__search-arrow"
            @click="handleClickDown"
          />
        </template>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { Ref, computed, inject, ref } from 'vue'

import { InputSize } from '@types'

import { scrollToNode } from '../utils/helpers'

import { UIPreloader, UIPanelSearch } from '@ui/core'
import { UIButtonIcon } from '@ui/buttons'
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/vue/24/outline'

type Props = {
  numberFound: number

  isLoading: boolean

  placeholder?: string

  size?: InputSize

  hideArrows?: boolean

  containerRef?: HTMLElement | null
}

const props = defineProps<Props>()

const modelValue = defineModel<string>({ default: '' })
const search = defineModel<string>('search', { default: '' })

const highlightedNodeIndex = inject<Ref<number>>('highlightedNodeIndex', ref(0))

const displayNumberFound = computed(
  () =>
    props.numberFound +
    ' result' +
    (props.numberFound > 1 || !props.numberFound ? 's' : ''),
)

const handleClickUp = () => {
  highlightedNodeIndex.value--
  if (highlightedNodeIndex.value < 1) {
    highlightedNodeIndex.value = props.numberFound
  }
  scrollToCurrentNode()
}

const handleClickDown = () => {
  highlightedNodeIndex.value++
  if (highlightedNodeIndex.value > props.numberFound) {
    highlightedNodeIndex.value = 1
  }
  scrollToCurrentNode()
}

const handleKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    handleClickDown()
  }
  if (e.key === 'Enter' && e.shiftKey) {
    handleClickUp()
  }
}

const scrollToCurrentNode = () => {
  if (!props.containerRef) return
  scrollToNode(props.containerRef, highlightedNodeIndex.value)
}
</script>

<style>
.ui-tree {
  &__search {
    @apply flex items-center justify-between;
    @apply shrink-0;
    @apply gap-x-2;
    @apply mb-2;
  }

  &__search-input {
    @apply w-full;
  }

  &__search-result {
    @apply flex items-center;
    @apply whitespace-nowrap;
    @apply text-gray-400;
    @apply text-sm sm:text-xs;
  }

  &__search-arrow {
    .ui-button {
      @apply focus:ring-offset-1 focus:ring-offset-body-gray;
    }
  }
}
</style>
